// src/components/Organizer/PayoutRequests.tsx
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { apiClient } from "../../utils/api";

interface PayoutRequest {
	id: string;
	amount: number;
	status: "pending" | "approved" | "rejected" | "paid";
	status_label: string;
	requested_at: string;
	processed_at: string | null;
	rejection_reason: string | null;
	bank_account: {
		bank_name: string;
		branch_name: string;
		account_type_label: string;
		account_number: string;
	} | null;
}

interface PayoutRequestsProps {
	availableAmount: number;
	onPayoutCompleted?: () => void;
}

const PayoutRequests: React.FC<PayoutRequestsProps> = ({
	availableAmount,
	onPayoutCompleted,
}) => {
	const [payoutRequests, setPayoutRequests] = useState<PayoutRequest[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [isCreating, setIsCreating] = useState(false);
	const [showCreateModal, setShowCreateModal] = useState(false);

	const fetchPayoutRequests = useCallback(async () => {
		setIsLoading(true);
		try {
			const response = await apiClient.get("/organizer/payout-requests");
			setPayoutRequests(response.data.payoutRequests || []);
		} catch (error) {
			console.error("Failed to fetch payout requests:", error);
			toast.error("入金申請の取得に失敗しました");
		} finally {
			setIsLoading(false);
		}
	}, []);

	useEffect(() => {
		void fetchPayoutRequests();
	}, [fetchPayoutRequests]);

	const handleCreateRequest = async () => {
		const amount = Math.round(availableAmount);

		if (amount <= 0) {
			toast.error("申請可能な金額がありません");
			return;
		}

		setIsCreating(true);
		try {
			await apiClient.post("/organizer/payout-requests", {
				amount,
			});
			toast.success("入金申請を送信しました");
			setShowCreateModal(false);
			onPayoutCompleted?.();
			await fetchPayoutRequests();
		} catch (error: any) {
			const errorMessage =
				error.response?.data?.message || "入金申請の送信に失敗しました";
			toast.error(errorMessage);
		} finally {
			setIsCreating(false);
		}
	};

	const handleCancelRequest = async (requestId: string) => {
		if (!window.confirm("この入金申請をキャンセルしますか?")) {
			return;
		}

		try {
			await apiClient.delete(`/organizer/payout-requests/${requestId}`);
			toast.success("入金申請をキャンセルしました");
			await fetchPayoutRequests();
		} catch (error: any) {
			const errorMessage =
				error.response?.data?.message || "キャンセルに失敗しました";
			toast.error(errorMessage);
		}
	};

	const getStatusBadgeColor = (status: string) => {
		switch (status) {
			case "pending":
				return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200";
			case "approved":
				return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200";
			case "rejected":
				return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
			case "paid":
				return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
			default:
				return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200";
		}
	};

	const formatDate = (dateString: string | null) => {
		if (!dateString) return "-";
		const date = new Date(dateString);
		return date.toLocaleString("ja-JP", {
			year: "numeric",
			month: "2-digit",
			day: "2-digit",
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	const hasPendingRequest = payoutRequests.some(
		(req) => req.status === "pending"
	);

	if (isLoading) {
		return (
			<div>
				<div className="flex items-center justify-center py-8">
					<div className="animate-spin h-8 w-8 border-4 border-violet-500 border-t-transparent rounded-full" />
				</div>
			</div>
		);
	}

	return (
		<>
			<div>
				<div className="flex items-center justify-between mb-4">
					<h3 className="text-lg font-semibold text-gray-900 dark:text-white">
						入金申請履歴
					</h3>
					<button
						onClick={() => setShowCreateModal(true)}
						disabled={hasPendingRequest || availableAmount <= 0}
						className="px-3 sm:px-4 py-2 bg-gradient-to-r from-violet-500 to-purple-600 text-white rounded-lg font-medium hover:from-violet-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
					>
						新規申請
					</button>
				</div>

				{hasPendingRequest && (
					<div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg">
						<p className="text-sm text-yellow-800 dark:text-yellow-200">
							申請中の入金申請があります。新しい申請を行うには、現在の申請が処理されるまでお待ちください。
						</p>
					</div>
				)}

				{payoutRequests.length === 0 ? (
					<div className="text-center py-8">
						<p className="text-gray-600 dark:text-gray-400">
							入金申請履歴がありません
						</p>
						<p className="text-sm text-gray-500 dark:text-gray-500 mt-2">
							売上が発生したら入金申請を行えます
						</p>
					</div>
				) : (
					<div className="space-y-3">
						{payoutRequests.map((request) => (
							<div
								key={request.id}
								className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
							>
								<div className="flex items-start justify-between">
									<div className="flex-1">
										<div className="flex items-center gap-3 mb-2">
											<span className="text-lg font-bold text-gray-900 dark:text-white">
												¥{Math.round(request.amount).toLocaleString("ja-JP")}
											</span>
											<span
												className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusBadgeColor(
													request.status
												)}`}
											>
												{request.status_label}
											</span>
										</div>
										<div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
											<p>申請日時: {formatDate(request.requested_at)}</p>
											{request.processed_at && (
												<p>処理日時: {formatDate(request.processed_at)}</p>
											)}
											{request.bank_account && (
												<p>
													振込先: {request.bank_account.bank_name}{" "}
													{request.bank_account.branch_name}支店{" "}
													{request.bank_account.account_type_label}{" "}
													{request.bank_account.account_number}
												</p>
											)}
											{request.rejection_reason && (
												<p className="text-red-600 dark:text-red-400">
													却下理由: {request.rejection_reason}
												</p>
											)}
										</div>
									</div>
									{request.status === "pending" && (
										<button
											onClick={() => handleCancelRequest(request.id)}
											className="ml-4 px-3 py-1 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
										>
											キャンセル
										</button>
									)}
								</div>
							</div>
						))}
					</div>
				)}
			</div>

			{/* 新規申請モーダル */}
			{showCreateModal && (
				<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
					<div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6">
						<h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
							入金申請
						</h3>

						<div className="mb-4 space-y-2">
							<p className="text-sm text-gray-600 dark:text-gray-400">
								入金予定金額
							</p>
							<div className="text-3xl font-bold text-gray-900 dark:text-white">
								¥{Math.round(availableAmount).toLocaleString("ja-JP")}
							</div>
							<p className="text-sm text-gray-700 dark:text-gray-300">
								この金額を全額申請します。
							</p>
							<p className="text-xs text-gray-500 dark:text-gray-400">
								※申請から入金まで通常3〜5営業日かかります
							</p>
						</div>

						<div className="flex gap-3">
							<button
								onClick={() => setShowCreateModal(false)}
								disabled={isCreating}
								className="flex-1 px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
							>
								キャンセル
							</button>
							<button
								onClick={handleCreateRequest}
								disabled={isCreating}
								className="flex-1 px-3 sm:px-4 py-2 bg-gradient-to-r from-violet-500 to-purple-600 text-white rounded-lg font-medium hover:from-violet-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
							>
								{isCreating ? "送信中..." : "入金申請"}
							</button>
						</div>
					</div>
				</div>
			)}
		</>
	);
};

export default PayoutRequests;
